import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 ────────────────────────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

const API_BASE = process.env.REACT_APP_API_URL || 'https://campus-life-production.up.railway.app/api';
const AI_BASE  = 'https://campus-life-ai.onrender.com';

const api = {
  getResumes:     async (userId)                     => { const res = await fetch(`${API_BASE}/resumes/${userId}`); if (!res.ok) throw new Error('목록 조회 실패'); return res.json(); },
  saveResume:     async (userId, data)               => { const res = await fetch(`${API_BASE}/resumes/${userId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); if (!res.ok) throw new Error('저장 실패'); return res.json(); },
  getResume:      async (resumeId)                   => { const res = await fetch(`${API_BASE}/resumes/detail/${resumeId}`); if (!res.ok) throw new Error('상세 조회 실패'); return res.json(); },
  saveAnalysis:   async (resumeId, analysis)         => { const res = await fetch(`${API_BASE}/resumes/${resumeId}/analysis`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ analysis }) }); if (!res.ok) throw new Error('분석 저장 실패'); return res.json(); },
  deleteResume:   async (resumeId)                   => { const res = await fetch(`${API_BASE}/resumes/${resumeId}`, { method: 'DELETE' }); if (!res.ok) throw new Error('삭제 실패'); return res.text(); },
  analyzeWithAI:  async (content, company, category) => { const res = await fetch(`${AI_BASE}/analyze`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content, company, category }) }); if (!res.ok) throw new Error('AI 분석 실패'); return res.json(); },
  getActivityData:async (userId)                     => { const [lectures, assignments, teams] = await Promise.all([fetch(`${API_BASE}/lectures/${userId}`).then(r => r.ok ? r.json() : []), fetch(`${API_BASE}/assignments/${userId}`).then(r => r.ok ? r.json() : []), fetch(`${API_BASE}/teams/${userId}`).then(r => r.ok ? r.json() : [])]); return { lectures, assignments, teams }; },
};

const getCurrentUserId   = () => { try { const u = JSON.parse(localStorage.getItem('currentUser')); return u?.userId || null; } catch { return null; } };
const getCurrentUserName = () => { try { const u = JSON.parse(localStorage.getItem('currentUser')); return u?.name || '사용자'; } catch { return '사용자'; } };
const formatDate = (createdAt) => { if (!createdAt) return ''; if (Array.isArray(createdAt)) { const [y, m, d] = createdAt; return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`; } if (typeof createdAt === 'string') return createdAt.split('T')[0]; return ''; };

const categories = [
  { name: '제조업',     color: '#dbeafe', textColor: '#1e40af' },
  { name: '마케팅 경영', color: '#fae8ff', textColor: '#7e22ce' },
  { name: 'IT/개발',   color: '#dcfce7', textColor: '#166534' },
  { name: '영업/유통',  color: '#ffedd5', textColor: '#c2410c' },
];

const borderColors = { '제조업': '#3b82f6', '마케팅 경영': '#a855f7', 'IT/개발': '#22c55e', '영업/유통': '#f97316' };
const getCatStyle = (catName) => { const cat = categories.find(c => c.name === catName) || categories[0]; return { backgroundColor: cat.color, color: cat.textColor }; };

const Ico = {
  File: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  List: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

const CoverLetterManager = () => {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();
  const [userId]  = useState(getCurrentUserId());

  const [letters,                  setLetters]                  = useState([]);
  const [selectedLetter,           setSelectedLetter]           = useState(null);
  const [isModalOpen,              setIsModalOpen]              = useState(false);
  const [newLetter,                setNewLetter]                = useState({ title: '', company: '', content: '', category: '제조업' });
  const [loading,                  setLoading]                  = useState(false);
  const [aiLoading,                setAiLoading]                = useState(false);
  const [saveStatus,               setSaveStatus]               = useState('');
  const [showDeleteConfirmModal,   setShowDeleteConfirmModal]   = useState(false);
  const [deleteTargetId,           setDeleteTargetId]           = useState(null);
  const [showSaveConfirmModal,     setShowSaveConfirmModal]     = useState(false);
  const [showAlertModal,           setShowAlertModal]           = useState(false);
  const [alertMessage,             setAlertMessage]             = useState('');
  const [alertType,                setAlertType]                = useState('normal');
  const [isGenerationModalOpen,    setIsGenerationModalOpen]    = useState(false);
  const [isGenerating,             setIsGenerating]             = useState(false);
  const [generationForm,           setGenerationForm]           = useState({ company: '', job: '' });
  const [generatedResult,          setGeneratedResult]          = useState('');

  const saveToLocal = (letter) => {
    const key      = `resumes_${userId}`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const newEntry = { ...letter, id: Date.now(), createdAt: new Date().toISOString(), isLocal: true };
    localStorage.setItem(key, JSON.stringify([newEntry, ...existing]));
    return newEntry;
  };

  const fetchLetters = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data      = await api.getResumes(userId);
      const localKey  = `resumes_${userId}`;
      const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
      const serverIds = new Set(data.map(d => String(d.id)));
      const localOnly = localData.filter(l => !serverIds.has(String(l.id)));
      const merged    = data.map(d => {
        const local = localData.find(l => String(l.id) === String(d.id));
        return { ...d, ...(local?.analysis ? { analysis: local.analysis } : {}), ...(local?.isFavorite !== undefined ? { isFavorite: local.isFavorite } : {}) };
      });
      setLetters([...merged, ...localOnly]);
    } catch {
      const localKey  = `resumes_${userId}`;
      const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
      setLetters(localData);
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchLetters(); }, [fetchLetters]);

  const handleAddNewLetter = async () => {
    if (!newLetter.title || !newLetter.content) { setAlertMessage('제목과 내용을 입력해주세요.'); setAlertType('normal'); setShowAlertModal(true); return; }
    setLoading(true);
    try {
      await api.saveResume(userId, newLetter);
      setIsModalOpen(false); setNewLetter({ title: '', company: '', content: '', category: '제조업' }); await fetchLetters();
    } catch {
      const localEntry = saveToLocal(newLetter);
      setLetters(prev => [localEntry, ...prev]);
      setIsModalOpen(false); setNewLetter({ title: '', company: '', content: '', category: '제조업' });
      setAlertMessage('임시 저장되었습니다.'); setAlertType('normal'); setShowAlertModal(true);
    } finally { setLoading(false); }
  };

  const deleteLetter = (e, resumeId) => { e.stopPropagation(); setDeleteTargetId(resumeId); setShowDeleteConfirmModal(true); };

  const confirmDeleteLetter = async () => {
    setShowDeleteConfirmModal(false);
    try { await api.deleteResume(deleteTargetId); } catch {}
    const localKey  = `resumes_${userId}`;
    const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
    localStorage.setItem(localKey, JSON.stringify(localData.filter(l => String(l.id) !== String(deleteTargetId))));
    setLetters(prev => prev.filter(l => String(l.id) !== String(deleteTargetId)));
    if (selectedLetter?.id === deleteTargetId) setSelectedLetter(null);
    setAlertMessage('삭제되었습니다.'); setAlertType('delete'); setShowAlertModal(true);
  };

  const toggleFavorite = (e, resumeId) => {
    e.stopPropagation();
    setLetters(prev => {
      const updated   = prev.map(l => l.id === resumeId ? { ...l, isFavorite: !l.isFavorite } : l);
      const localKey  = `resumes_${userId}`;
      const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
      const target    = updated.find(l => String(l.id) === String(resumeId));
      const isFav     = target?.isFavorite;
      const updatedLocal = localData.map(l => String(l.id) === String(resumeId) ? { ...l, isFavorite: isFav } : l);
      if (!updatedLocal.find(l => String(l.id) === String(resumeId))) updatedLocal.unshift({ id: resumeId, isFavorite: isFav });
      localStorage.setItem(localKey, JSON.stringify(updatedLocal));
      return updated;
    });
  };

  const handleContentChange = (e) => { setSelectedLetter(prev => ({ ...prev, content: e.target.value, analysis: null })); setSaveStatus(''); };
  const triggerSave = () => setShowSaveConfirmModal(true);

  const handleSave = async () => {
    setShowSaveConfirmModal(false);
    if (!selectedLetter) return;
    setSaveStatus('saving');
    try {
      await api.deleteResume(selectedLetter.id);
      const saved = await api.saveResume(userId, { title: selectedLetter.title, company: selectedLetter.company, category: selectedLetter.category, content: selectedLetter.content });
      setSelectedLetter(saved); setSaveStatus('saved'); await fetchLetters(); setTimeout(() => setSaveStatus(''), 2000);
      setAlertMessage('성공적으로 저장되었습니다!'); setAlertType('normal'); setShowAlertModal(true);
    } catch {
      const localKey  = `resumes_${userId}`;
      const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
      const updated   = localData.map(l => String(l.id) === String(selectedLetter.id) ? { ...l, content: selectedLetter.content, title: selectedLetter.title } : l);
      if (!updated.find(l => String(l.id) === String(selectedLetter.id))) updated.unshift({ ...selectedLetter, isLocal: true });
      localStorage.setItem(localKey, JSON.stringify(updated));
      setLetters(prev => prev.map(l => String(l.id) === String(selectedLetter.id) ? { ...l, ...selectedLetter } : l));
      setSaveStatus('saved'); setTimeout(() => setSaveStatus(''), 2000);
      setAlertMessage('임시 저장되었습니다.'); setAlertType('normal'); setShowAlertModal(true);
    }
  };

  const handleAIAnalysis = async (resumeId) => {
    if (!selectedLetter) return;
    setAiLoading(true);
    try {
      let analysisText = '';
      try {
        const aiResult = await api.analyzeWithAI(selectedLetter.content, selectedLetter.company, selectedLetter.category);
        analysisText = aiResult.analysis || JSON.stringify(aiResult);
      } catch {
        const { content, company, category } = selectedLetter;
        analysisText = [`[${company} 지원 보완점] ${content.length > 200 ? '문장이 다소 길어 가독성이 떨어집니다.' : '내용이 다소 빈약합니다.'} 역량 증명이 추가되어야 합니다.`, `[${category} 직무 강점] ${category} 분야에서 요구하는 기초 소양과 실무 이해도가 잘 드러납니다.`, `[맞춤형 수정 방향] ${company}의 인재상에 맞춰 결론 부분에 조직 융화력을 강조하는 문장을 추가하면 좋겠습니다.`].join('\n\n');
      }
      try {
        const updated = await api.saveAnalysis(resumeId, analysisText);
        setSelectedLetter(updated); await fetchLetters();
      } catch {
        setSelectedLetter(prev => ({ ...prev, analysis: analysisText }));
        setLetters(prev => prev.map(l => String(l.id) === String(resumeId) ? { ...l, analysis: analysisText } : l));
      }
      const localKey     = `resumes_${userId}`;
      const localData    = JSON.parse(localStorage.getItem(localKey) || '[]');
      const updatedLocal = localData.map(l => String(l.id) === String(resumeId) ? { ...l, analysis: analysisText } : l);
      if (!updatedLocal.find(l => String(l.id) === String(resumeId))) updatedLocal.unshift({ id: resumeId, analysis: analysisText });
      localStorage.setItem(localKey, JSON.stringify(updatedLocal));
    } finally { setAiLoading(false); }
  };

  const openGenerationModal = async () => {
    setLoading(true);
    try { await api.getActivityData(userId); setGeneratedResult(''); setIsGenerationModalOpen(true); }
    catch { setAlertMessage('활동 데이터를 가져오는데 실패했습니다.'); setAlertType('error'); setShowAlertModal(true); }
    finally { setLoading(false); }
  };

  const handleGenerateResume = async () => {
    if (!generationForm.company || !generationForm.job) { setAlertMessage('회사와 직무를 입력해주세요.'); setShowAlertModal(true); return; }
    setIsGenerating(true);
    try {
      const res = await fetch(`${AI_BASE}/generate-resume`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetCompany: generationForm.company, targetJob: generationForm.job }) });
      if (!res.ok) throw new Error('생성 실패');
      const result = await res.json();
      if (result.content) setGeneratedResult(result.content); else throw new Error('내용 없음');
    } catch { setAlertMessage('AI 자소서 생성에 실패했습니다. 잠시 후 다시 시도해주세요.'); setAlertType('error'); setShowAlertModal(true); }
    finally { setIsGenerating(false); }
  };

  const handleAddGeneratedToList = async () => {
    setLoading(true);
    const generated = { title: `[AI초안] ${generationForm.company} ${generationForm.job}`, company: generationForm.company, content: generatedResult, category: 'IT/개발' };
    try {
      await api.saveResume(userId, generated);
      setIsGenerationModalOpen(false); setGeneratedResult(''); await fetchLetters();
      setAlertMessage('자소서 리스트에 추가되었습니다!'); setShowAlertModal(true);
    } catch {
      const localEntry = saveToLocal(generated);
      setLetters(prev => [localEntry, ...prev]);
      setIsGenerationModalOpen(false); setGeneratedResult('');
      setAlertMessage('자소서 리스트에 추가되었습니다!'); setAlertType('normal'); setShowAlertModal(true);
    } finally { setLoading(false); }
  };

  const handleSelectLetter = async (letter) => {
    try {
      const detail    = await api.getResume(letter.id);
      const localKey  = `resumes_${userId}`;
      const localData = JSON.parse(localStorage.getItem(localKey) || '[]');
      const localItem = localData.find(l => String(l.id) === String(letter.id));
      setSelectedLetter(localItem?.analysis && !detail.analysis ? { ...detail, analysis: localItem.analysis } : detail);
      setSaveStatus('');
    } catch { setSelectedLetter(letter); }
  };

  const renderAnalysis = (analysis) => {
    if (!analysis) return null;
    const renderInline = (text) => { const parts = text.split(/\*\*(.+?)\*\*/g); return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p); };
    const isHeading    = (line) => line.startsWith('### ') || line.startsWith('## ');
    const stripHeading = (line) => line.replace(/^#{2,3}\s*/, '');
    const lines  = analysis.split('\n');
    const blocks = [];
    let current  = [];
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed === '') { if (current.length > 0) { blocks.push([...current]); current = []; } } else { current.push(trimmed); }
    });
    if (current.length > 0) blocks.push(current);
    return blocks.map((block, i) => {
      if (block.length === 1 && isHeading(block[0])) {
        return <div key={i} style={{ marginBottom: '12px', marginTop: i > 0 ? '16px' : 0 }}><h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: BLUE }}>{renderInline(stripHeading(block[0]))}</h4></div>;
      }
      return (
        <div key={i} style={{ marginBottom: '10px', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <p style={{ margin: 0, lineHeight: '1.7', fontSize: '12px', color: '#374151' }}>{renderInline(block.join(' '))}</p>
        </div>
      );
    });
  };

  return (
    <div style={{
      ...S.root,
      padding: isMobile ? '16px 14px 60px' : '32px 32px 60px',
      overflowX: 'hidden',
    }}>
      <div style={S.inner}>

        {/* ── 헤더 (모바일: 세로 배치) ── */}
        <div style={{
          ...S.header,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '10px' : '0',
        }}>
          <h1 style={{ ...S.pageTitle, fontSize: isMobile ? '17px' : '22px' }}>📄 AI 자소서 매니저</h1>
          <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto' }}>
            <button style={{ ...S.primaryBtn, flex: isMobile ? 1 : 'none' }} onClick={() => setIsModalOpen(true)}>+ 새 자소서 작성</button>
            <button style={{ ...S.aiBtn, flex: isMobile ? 1 : 'none' }} onClick={openGenerationModal}>✨ AI 자소서 생성</button>
          </div>
        </div>

        {loading && !letters.length && <p style={S.emptyText}>⏳ 데이터를 처리하는 중...</p>}

        {!loading && letters.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <p style={{ color: '#94a3b8', fontSize: '15px', marginBottom: '16px' }}>작성된 자기소개서가 없어요!</p>
            <button style={S.primaryBtn} onClick={() => setIsModalOpen(true)}>+ 새 자소서 작성</button>
          </div>
        )}

        {/* ── 자소서 카드 그리드 ── */}
        {letters.length > 0 && (
          <div style={S.card}>
            <CardHeader icon={Ico.List} title="내 자소서 목록" />
            <div style={{
              ...S.grid,
              // 모바일: 1열
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            }}>
              {letters.map(letter => (
                <div
                  key={letter.id}
                  style={{ ...S.letterCard, borderLeft: `4px solid ${borderColors[letter.category] || BLUE}` }}
                  onClick={() => handleSelectLetter(letter)}
                >
                  <span style={S.starIcon} onClick={e => toggleFavorite(e, letter.id)}>{letter.isFavorite ? '⭐' : '☆'}</span>
                  <span style={S.delIcon}  onClick={e => deleteLetter(e, letter.id)}>🗑️</span>
                  <span style={{ ...S.catTag, ...getCatStyle(letter.category) }}>{letter.category || '기타'}</span>
                  <p style={S.letterTitle}>{letter.title || '(제목 없음)'}</p>
                  <p style={S.letterMeta}>
                    <span style={{ color: BLUE, fontWeight: '600' }}>{letter.company}</span>
                    {letter.company && <span style={{ color: '#94a3b8' }}> | </span>}
                    <span style={{ color: '#94a3b8' }}>{formatDate(letter.createdAt)}</span>
                  </p>
                  {letter.analysis
                    ? <p style={S.analysisBadge}>✓ 분석 완료</p>
                    : <p style={{ ...S.analysisBadge, color: '#94a3b8' }}>○ 분석 미완료</p>
                  }
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 상세 보기 ── */}
        {selectedLetter && (
          <div style={S.card}>
            <div style={{
              ...S.detailHeader,
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '10px' : '0',
            }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#1e293b' }}>{selectedLetter.title || '(제목 없음)'}</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {saveStatus === 'saving' && <span style={{ fontSize: '11px', color: '#94a3b8' }}>저장 중...</span>}
                {saveStatus === 'saved'  && <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: '600' }}>✓ 저장됨</span>}
                {saveStatus === 'error'  && <span style={{ fontSize: '11px', color: '#ef4444' }}>저장 실패</span>}
                <button style={S.saveBtn}  onClick={triggerSave} disabled={saveStatus === 'saving'}>저장</button>
                <button style={S.closeBtn} onClick={() => setSelectedLetter(null)}>닫기</button>
              </div>
            </div>

            {/* 본문 + AI분석 (모바일: 세로 배치) */}
            <div style={{
              ...S.detailBody,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '14px' : '0',
            }}>
              {/* 본문 */}
              <div style={{ display: 'flex', flexDirection: 'column', flex: isMobile ? 'none' : '0 0 58%', gap: '10px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>본문 작성</p>
                <textarea
                  style={{ ...S.textarea, height: isMobile ? '220px' : '340px' }}
                  value={selectedLetter.content || ''}
                  onChange={handleContentChange}
                  placeholder="내용을 입력하세요..."
                />
                <button
                  style={{ ...S.primaryBtn, width: '100%', padding: '12px', opacity: aiLoading ? 0.6 : 1 }}
                  onClick={() => handleAIAnalysis(selectedLetter.id)}
                  disabled={aiLoading}
                >
                  {aiLoading ? '⏳ AI 분석 중...' : 'AI 심층 분석 시작'}
                </button>
              </div>

              {/* 구분선 (데스크탑만) */}
              {!isMobile && (
                <div style={{ width: '1px', backgroundColor: '#e2e8f0', margin: '0 20px', alignSelf: 'stretch' }} />
              )}

              {/* AI 분석 결과 */}
              <div style={{
                ...S.analysisBox,
                maxHeight: isMobile ? '300px' : '440px',
              }}>
                <p style={{ fontSize: '13px', fontWeight: '700', color: BLUE, marginBottom: '12px' }}>🤖 AI 분석 결과</p>
                {selectedLetter.analysis
                  ? <div>{renderAnalysis(selectedLetter.analysis)}</div>
                  : <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px', marginTop: '20px' }}>수정된 내용을 바탕으로<br />새로운 분석을 시작하세요.</p>
                }
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 새 자소서 모달 ── */}
      {isModalOpen && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>새 자기소개서 추가</h3>
            <input style={S.modalInput} placeholder="제목 (최대 50자)" value={newLetter.title} onChange={e => { if (e.target.value.length <= 50) setNewLetter({ ...newLetter, title: e.target.value }); }} maxLength={50} />
            <input style={S.modalInput} placeholder="지원 회사 (최대 30자)" value={newLetter.company} onChange={e => { if (e.target.value.length <= 30) setNewLetter({ ...newLetter, company: e.target.value }); }} maxLength={30} />
            <select style={S.modalInput} value={newLetter.category} onChange={e => setNewLetter({ ...newLetter, category: e.target.value })}>
              {categories.map(cat => <option key={cat.name} value={cat.name}>{cat.name}</option>)}
            </select>
            <textarea style={{ ...S.modalInput, height: '180px', resize: 'vertical' }} placeholder="내용을 입력하세요 (최대 2000자)" value={newLetter.content} onChange={e => { if (e.target.value.length <= 2000) setNewLetter({ ...newLetter, content: e.target.value }); }} maxLength={2000} />
            <div style={S.modalBtnRow}>
              <button style={{ ...S.primaryBtn, flex: 1, padding: '12px', opacity: loading ? 0.6 : 1 }} onClick={handleAddNewLetter} disabled={loading}>{loading ? '저장 중...' : '저장하기'}</button>
              <button style={S.secondaryBtn} onClick={() => setIsModalOpen(false)}>취소</button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI 자소서 생성 모달 ── */}
      {isGenerationModalOpen && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '460px', textAlign: 'left' }}>
            <h3 style={{ ...S.modalTitle, textAlign: 'left' }}>✨ AI 자소서 생성</h3>
            {!generatedResult ? (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={S.modalLabel}>지원 대상 회사</label>
                  <input style={S.modalInput} placeholder="예: 네이버 (최대 30자)" value={generationForm.company} onChange={e => { if (e.target.value.length <= 30) setGenerationForm({ ...generationForm, company: e.target.value }); }} maxLength={30} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={S.modalLabel}>지원 직무</label>
                  <input style={S.modalInput} placeholder="예: 마케팅 (최대 20자)" value={generationForm.job} onChange={e => { if (e.target.value.length <= 20) setGenerationForm({ ...generationForm, job: e.target.value }); }} maxLength={20} />
                </div>
              </>
            ) : (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: '#6c5ce7', fontWeight: '600', marginBottom: '8px' }}>✨ AI가 작성한 초안입니다.</p>
                <textarea style={{ ...S.modalInput, height: '260px', fontSize: '13px', backgroundColor: '#f8faff', resize: 'vertical' }} value={generatedResult} readOnly />
              </div>
            )}
            <div style={S.modalBtnRow}>
              <button style={S.secondaryBtn} onClick={() => { setIsGenerationModalOpen(false); setGeneratedResult(''); }}>닫기</button>
              {!generatedResult
                ? <button style={{ ...S.primaryBtn, flex: 1, padding: '12px', backgroundColor: '#6c5ce7' }} onClick={handleGenerateResume} disabled={isGenerating}>{isGenerating ? 'AI가 쓰는 중...' : '생성 시작!'}</button>
                : <button style={{ ...S.primaryBtn, flex: 1, padding: '12px', backgroundColor: '#22c55e' }} onClick={handleAddGeneratedToList}>자소서 추가하기</button>
              }
            </div>
          </div>
        </div>
      )}

      {/* ── 삭제 확인 모달 ── */}
      {showDeleteConfirmModal && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '280px' }}>
            <p style={S.modalMsg}>정말 삭제하시겠습니까?</p>
            <div style={S.modalBtnRow}>
              <button style={S.secondaryBtn} onClick={() => setShowDeleteConfirmModal(false)}>취소</button>
              <button style={{ ...S.primaryBtn, flex: 1, padding: '12px', backgroundColor: '#ef4444' }} onClick={confirmDeleteLetter}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 저장 확인 모달 ── */}
      {showSaveConfirmModal && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '280px' }}>
            <p style={S.modalMsg}>저장하시겠습니까?</p>
            <div style={S.modalBtnRow}>
              <button style={S.secondaryBtn} onClick={() => setShowSaveConfirmModal(false)}>취소</button>
              <button style={{ ...S.primaryBtn, flex: 1, padding: '12px' }} onClick={handleSave}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 알림 모달 ── */}
      {showAlertModal && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '280px' }}>
            <p style={S.modalMsg}>{alertMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button style={{ ...S.primaryBtn, padding: '10px 32px', backgroundColor: alertType === 'delete' || alertType === 'error' ? '#ef4444' : BLUE }} onClick={() => setShowAlertModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh', backgroundColor: '#f8fafc',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '32px 32px 60px', boxSizing: 'border-box',
  },
  inner:      { width: '100%', maxWidth: '1100px', display: 'flex', flexDirection: 'column', gap: '20px' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  pageTitle:  { fontSize: '22px', fontWeight: '800', color: '#1e293b', margin: 0 },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '20px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', boxSizing: 'border-box',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  cardIcon:   { width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: BLUE_BG, color: BLUE },
  cardTitle:  { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  grid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' },
  letterCard: { backgroundColor: '#fff', borderRadius: '10px', padding: '16px 18px', border: '1px solid #f1f5f9', cursor: 'pointer', position: 'relative', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', minHeight: '130px', boxSizing: 'border-box' },
  catTag:       { display: 'inline-block', padding: '3px 9px', borderRadius: '5px', fontSize: '11px', fontWeight: '700', marginBottom: '8px' },
  letterTitle:  { fontSize: '13px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' },
  letterMeta:   { fontSize: '11px', margin: 0 },
  analysisBadge:{ fontSize: '11px', fontWeight: '600', color: '#22c55e', marginTop: '8px' },
  starIcon:     { position: 'absolute', top: '12px', right: '12px', fontSize: '16px', cursor: 'pointer' },
  delIcon:      { position: 'absolute', top: '34px', right: '12px', fontSize: '14px', opacity: 0.5, cursor: 'pointer' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  detailBody:   { display: 'flex', gap: '0', alignItems: 'stretch' },
  textarea:     { width: '100%', height: '340px', padding: '12px', fontSize: '13px', lineHeight: '1.6', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif", backgroundColor: '#f8fafc', color: '#374151' },
  analysisBox:  { flex: 1, backgroundColor: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe', padding: '16px', overflowY: 'auto', maxHeight: '440px', boxSizing: 'border-box' },
  primaryBtn:   { padding: '9px 16px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  aiBtn:        { padding: '9px 16px', backgroundColor: '#6c5ce7', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  secondaryBtn: { flex: 1, padding: '12px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  saveBtn:      { padding: '7px 14px', backgroundColor: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  closeBtn:     { padding: '7px 14px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  emptyText:    { textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '30px 0', margin: 0 },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 },
  modal:        { backgroundColor: '#fff', padding: '28px 32px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', width: '90%', maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: '10px', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle:   { fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 },
  modalMsg:     { fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '0 0 8px', textAlign: 'center' },
  modalLabel:   { fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' },
  modalInput:   { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#374151', boxSizing: 'border-box', outline: 'none', backgroundColor: '#f8fafc', fontFamily: 'inherit' },
  modalBtnRow:  { display: 'flex', gap: '10px', marginTop: '6px' },
};

export default CoverLetterManager;